import { IOption } from "../../../types";
import {
  NavItem,
  SetupListNavInfoByDepartment,
} from "../../../types/types.navigation";
import { IconRepairOrder, IconAccounting } from "@cdkglobal/radial";
import { DEPARTMENT_SUBGROUPS_MAP } from "../constants";
import { ampli } from "../../../ampli";

export const iconMap: Record<string, React.ComponentType> = {
  accounting: IconAccounting,
};

export const isLoadingNavItem = {
  id: "Loading...",
  text: "Loading...",
  isDisabled: true,
};

export const noResultsNavItem = {
  id: "No Results",
  text: "No Results",
  isDisabled: true,
};

type NavGroupConfig = {
  id: string;
  text: string;
  icon: JSX.Element;
  departmentValues: string[];
};

const NAV_GROUPS: NavGroupConfig[] = [
  {
    id: "fixedops",
    text: "Fixed Operations",
    icon: <IconRepairOrder />,
    departmentValues: ["parts", "service", "general"],
  },
];

const getTooltip = ({
  item,
  namespace,
}: {
  item: NavItem;
  namespace: string;
}) => ({
  tooltipProps: {
    id: `${namespace}-${item.id}-tooltip`,
    text: item.text,
  },
});

const addTooltipToNavItems = ({
  items,
  namespace,
}: {
  items: NavItem[];
  namespace: string;
}): NavItem[] => {
  return items.map((item) => ({
    ...item,
    tooltip: getTooltip({ item, namespace }),
    items: item.items
      ? addTooltipToNavItems({
          items: item.items,
          namespace: `${namespace}-${item.id}`,
        })
      : undefined,
  }));
};

// Returns the Navigation Section for a department. (Amplitude)
const getNavigationSection = (departmentValue: string): string | undefined => {
  return NAV_GROUPS.find((group) =>
    group.departmentValues.includes(departmentValue),
  )?.text;
};

const getDepartmentNavItem = ({
  department,
  setupListNavInfoByDepartment,
  handleSetupClick,
}: {
  department: IOption;
  setupListNavInfoByDepartment: SetupListNavInfoByDepartment;
  handleSetupClick: (params: { setup: IOption; department: IOption }) => void;
}): NavItem => {
  const Icon = iconMap[department.value];
  const { setups } = setupListNavInfoByDepartment[department.value] || {};
  const navigationSection = getNavigationSection(department.value);

  let subNavItems: NavItem[];

  if (setups && setups.length > 0) {
    const setupNavItems = setups.map((setupItem: NavItem) => ({
      ...setupItem,
      onClick: () => {
        ampli.selectNavigation({
          "Navigation Item Clicked": true,
          ...(navigationSection
            ? { "Navigation Section": navigationSection }
            : {}),
          "Navigation Item": department.label,
          Setup: setupItem.text,
        });
        handleSetupClick({
          setup: { value: setupItem.id, label: setupItem.text },
          department,
        });
      },
    }));

    subNavItems = setupNavItems.length
      ? getDepartmentSubGroupItems(department, setupNavItems)
      : [noResultsNavItem];
  } else {
    subNavItems = [noResultsNavItem];
  }

  return {
    id: department.value,
    text: department.label,
    icon: Icon ? <Icon /> : undefined,
    items: subNavItems,
  };
};

const groupDepartmentNavItems = ({
  departmentNavItems,
}: {
  departmentNavItems: NavItem[];
}): NavItem[] => {
  const groupedDepartmentIds = new Set(
    NAV_GROUPS.flatMap((group) => group.departmentValues),
  );

  const ungroupedItems = departmentNavItems.filter(
    (item) => !groupedDepartmentIds.has(item.id),
  );

  const groupedItems = NAV_GROUPS.flatMap((group) => {
    const childItems = departmentNavItems.filter((item) =>
      group.departmentValues.includes(item.id),
    );

    if (!childItems.length) {
      return [];
    }

    return [
      {
        id: group.id,
        text: group.text,
        icon: group.icon,
        items: childItems,
      },
    ];
  });

  return [...ungroupedItems, ...groupedItems];
};

export const getDepartmentNavigationListItems = ({
  departments,
  setupListNavInfoByDepartment,
  isFetchingUserData,
  handleSetupClick,
}: {
  departments: IOption[];
  setupListNavInfoByDepartment: SetupListNavInfoByDepartment;
  isFetchingUserData: boolean;
  handleSetupClick: (params: { setup: IOption; department: IOption }) => void;
}): NavItem[] => {
  if (isFetchingUserData) {
    return [isLoadingNavItem];
  }

  if (!departments.length) {
    return [noResultsNavItem];
  }

  const departmentNavItems = departments
    .filter(
      (department) =>
        (setupListNavInfoByDepartment[department.value]?.setups?.length ?? 0) >
        0,
    )
    .map((department) =>
      getDepartmentNavItem({
        department,
        setupListNavInfoByDepartment,
        handleSetupClick,
      }),
    );

  const groupedItems = groupDepartmentNavItems({ departmentNavItems });
  return addTooltipToNavItems({
    items: groupedItems,
    namespace: "setup-selector-navigation-menu",
  });
};

export const getDepartmentSubGroupItems = (
  department: IOption,
  items: NavItem[],
): NavItem[] => {
  const subgroups = DEPARTMENT_SUBGROUPS_MAP.get(department.value);
  if (!subgroups) return items;
  const subGroupItems: NavItem[] = [];

  subgroups.forEach((subGroup) => {
    const matchedItems = items.filter((item) =>
      subGroup.setupsList.includes(item.id),
    );
    if (matchedItems.length) {
      subGroupItems.push({
        id: subGroup.id,
        text: subGroup.text,
        items: matchedItems,
      });
    }
  });

  const subgroupSetup = subgroups.flatMap((sg) => sg.setupsList);
  const remainingSetups = items.filter(
    (item) => !subgroupSetup.includes(item.id),
  );

  return [...subGroupItems, ...remainingSetups];
};
